import type { AccountFilters } from '@/graphql/generated/schema'
import type { AccountFilters as PreMigrationAccountFilters } from '@/graphql/generated/schema.pre-account-migration'
import type { SormFilters } from '../account-query-sorm-types'

import {
  BOOLEAN_ACCOUNT_FILTER_KEYS,
  MOODYS_RATING_RANKS,
  NO_MATCH_FILTER_VALUE,
  NON_TAXABLE_TAX_STATUSES,
  NUMERICAL_ACCOUNT_FILTER_KEYS,
  SORM_ACCOUNT_FIELD_BY_PUBLIC_FIELD,
  SP_RATING_RANKS,
  STRING_ACCOUNT_FILTER_KEYS,
  SYNC_STATUS_BY_MOMENT_STATUS,
  TAXABLE_TAX_STATUS,
} from '../constants'

type AccountQueryFilters = AccountFilters | PreMigrationAccountFilters

interface StringFilterInput {
  equal_to?: string | null
  in?: string[] | null
  includes?: string | null
}

interface BooleanFilterInput {
  equal_to?: boolean | null
}

interface NumericalFilterInput {
  less_than?: number | null
  less_than_or_equal_to?: number | null
  greater_than?: number | null
  greater_than_or_equal_to?: number | null
}

const NUMERICAL_OPERATORS = [
  'less_than',
  'less_than_or_equal_to',
  'greater_than',
  'greater_than_or_equal_to',
] as const

const RATING_OPERATORS = ['equal_to', ...NUMERICAL_OPERATORS] as const

type RatingFilterInput = Partial<
  Record<(typeof RATING_OPERATORS)[number], string | null>
>

const STRING_KEYS = new Set<string>(STRING_ACCOUNT_FILTER_KEYS)
const BOOLEAN_KEYS = new Set<string>(BOOLEAN_ACCOUNT_FILTER_KEYS)
const NUMERICAL_KEYS = new Set<string>(NUMERICAL_ACCOUNT_FILTER_KEYS)

function accountKey(field: string): string {
  return `Account.${SORM_ACCOUNT_FIELD_BY_PUBLIC_FIELD[field] ?? field}`
}

function stringCondition(
  filter: StringFilterInput,
): Record<string, unknown> | null {
  const condition: Record<string, unknown> = {}
  if (filter.equal_to != null) {
    condition.equal_to = filter.equal_to
  }
  if (filter.in != null && filter.in.length > 0) {
    condition.in = filter.in
  }
  if (filter.includes != null) {
    condition.includes_case_insensitive = filter.includes
  }
  return Object.keys(condition).length > 0 ? condition : null
}

function numericalCondition(
  filter: NumericalFilterInput,
): Record<string, unknown> | null {
  const condition: Record<string, unknown> = {}
  for (const operator of NUMERICAL_OPERATORS) {
    const value = filter[operator]
    if (value != null) {
      condition[operator] = value
    }
  }
  return Object.keys(condition).length > 0 ? condition : null
}

function ratingCondition(
  filter: RatingFilterInput,
  ranks: Readonly<Record<string, number>>,
): Record<string, unknown> | null {
  const condition: Record<string, unknown> = {}
  for (const operator of RATING_OPERATORS) {
    const rating = filter[operator]
    const rank = rating == null ? undefined : ranks[rating]
    if (rank !== undefined) {
      condition[operator] = rank
    }
  }
  return Object.keys(condition).length > 0 ? condition : null
}

function momentStatusCondition(
  filter: StringFilterInput,
): Record<string, unknown> | null {
  const translated: StringFilterInput = {}
  let hasInput = filter.includes != null
  if (filter.equal_to != null) {
    hasInput = true
    const syncStatus = SYNC_STATUS_BY_MOMENT_STATUS[filter.equal_to]
    if (syncStatus) {
      translated.equal_to = syncStatus
    }
  }
  if (filter.in != null && filter.in.length > 0) {
    hasInput = true
    const syncStatuses = filter.in.flatMap((status) => {
      const syncStatus = SYNC_STATUS_BY_MOMENT_STATUS[status]
      return syncStatus ? [syncStatus] : []
    })
    if (syncStatuses.length > 0) {
      translated.in = syncStatuses
    }
  }
  if (hasInput && translated.equal_to == null && translated.in == null) {
    translated.equal_to = NO_MATCH_FILTER_VALUE
  }
  return stringCondition(translated)
}

function isTaxableCondition(
  filter: BooleanFilterInput,
): Record<string, unknown> | null {
  if (filter.equal_to == null) {
    return null
  }
  return filter.equal_to
    ? { equal_to: TAXABLE_TAX_STATUS }
    : { in: NON_TAXABLE_TAX_STATUSES }
}

function translateFilterEntry(
  key: string,
  value: unknown,
): [string, unknown] | null {
  if (STRING_KEYS.has(key)) {
    const condition = stringCondition(value as StringFilterInput)
    return condition ? [accountKey(key), condition] : null
  }
  if (BOOLEAN_KEYS.has(key)) {
    const equalTo = (value as BooleanFilterInput).equal_to
    return equalTo == null ? null : [accountKey(key), { equal_to: equalTo }]
  }
  if (NUMERICAL_KEYS.has(key)) {
    const condition = numericalCondition(value as NumericalFilterInput)
    return condition ? [accountKey(key), condition] : null
  }
  switch (key) {
    case 'or': {
      const branches = value as AccountQueryFilters[]
      return branches.length > 0
        ? ['or', branches.map((branch) => translateAccountFilters(branch))]
        : null
    }
    case 'is_taxable': {
      const condition = isTaxableCondition(value as BooleanFilterInput)
      return condition ? [accountKey(key), condition] : null
    }
    case 'average_moodys_rating': {
      const condition = ratingCondition(
        value as RatingFilterInput,
        MOODYS_RATING_RANKS,
      )
      return condition
        ? ['Account.average_moodys_rating_rank', condition]
        : null
    }
    case 'average_sp_rating': {
      const condition = ratingCondition(
        value as RatingFilterInput,
        SP_RATING_RANKS,
      )
      return condition ? ['Account.average_sp_rating_rank', condition] : null
    }
    case 'model_id': {
      const ids = (value as { in?: string[] | null }).in
      return ids != null && ids.length > 0
        ? ['Sleeve.strategy_id', { in: ids }]
        : null
    }
    case 'moment_status': {
      const condition = momentStatusCondition(value as StringFilterInput)
      return condition ? ['Account.sync_status', condition] : null
    }
    default:
      throw new Error(`Unsupported account filter: ${key}`)
  }
}

/**
 * Translates legacy account GraphQL filters into a SORM `Account` filter,
 * matching public-api's account GraphQL resolver. Keys that resolver's schema
 * rejects throw instead of being dropped, so a filter is never silently widened.
 */
export function translateAccountFilters(
  filters: AccountQueryFilters,
): SormFilters {
  const sormFilters: SormFilters = {}
  for (const [key, value] of Object.entries(filters)) {
    if (value == null) {
      continue
    }
    const entry = translateFilterEntry(key, value)
    if (entry) {
      sormFilters[entry[0]] = entry[1]
    }
  }
  return sormFilters
}
